import React, { useCallback, useEffect, useState } from 'react';
import { useModal } from 'react-modal-hook';
import { useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';
import { IApiQueryOptions } from 'api/ApiClient';
import { FieldWrapper, Filter, FilterButton, Input, PaginatedList, PaymentOverviewStats, PaymentStatusPills, PayrollTipsTable, Section, SectionHeader, Text } from 'components';
import ButtonExportPayrollTipsCSVAsync from 'components/Button/ButtonExportPayrollTipsCsvAsync';
import { lineHeight, theme, toRem } from 'design';
import { getQueryStringValue, useDebounce, usePayPeriods, useQueryString } from 'hooks';
import { ClosePayPeriodModal, ReopenPayPeriodModal } from 'modals';
import { selectIsLoadingAPIRequest } from 'store/ApiClient';
import { selectCurrentEmployee } from 'store/App';
import { EmployeePayrollTips, getEmployeePayrollTipsRequest } from 'store/Collections';
import { selectEmployeePayrollTips } from 'store/Collections/EmployeePayrollTips/selectors';
import { selectIsPermitted } from 'store/Employers';
import { selectSite } from 'store/Site';
import { getSitePayoutStatsRequest, selectPayoutStats, Stats } from 'store/Stats';
import { IStore } from 'store/types';
import { ApiQueryOperator, EmployeeRole, IApiQueryParam, IPayPeriod, PayoutStatus } from 'types';
import { getTodayWithSiteTimezone } from 'utils/payment';
import { hasCloseChangedStats, payPeriodDateFilters } from 'utils/payPeriods';
import { DEFAULT_OFFSET, DEFAULT_PAGE_SIZE } from 'utils/table';
import { PayPeriodBanner, PayPeriodSelector, PayrollPeriodWrapper } from './components';

const StyledSectionHeader = styled(SectionHeader)`
  width: 100%;

  @media (min-width: ${theme.breakpoint.med}px) {
    flex-flow: row wrap;
  }

  section {
    width: 100%;

    @media (min-width: ${theme.breakpoint.med}px) {
      width: auto;
      margin-left: 0;
      flex-flow: row wrap;
    }
  }
`;

const PayrollPeriodSection = styled.div`
  display: flex;
  flex-flow: row wrap;
  align-items: center;
  width: 100%;
  gap: ${toRem(theme.space.sm)};
`;

// Filters and Export wrap together, leaving room for the export menu to open leftward.
const HeaderActions = styled.div`
  align-items: center;
  gap: ${toRem(theme.space.sm)};
`;

const PayrollPaymentLabel = styled(Text).attrs({
  as: 'label',
})`
  font-size: ${toRem(theme.font.size.xxs)};
  line-height: ${lineHeight(theme.font.size.xxs)};
  font-weight: ${theme.font.weight.medium};
  display: none;

  @media (min-width: ${theme.breakpoint.small}px) {
    font-size: ${toRem(theme.font.size.sm)};
    line-height: ${lineHeight(theme.font.size.sm)};
    white-space: nowrap;
    display: block;
  }
`;

const DEFAULT_SORT = 'employee_name';
const DEFAULT_STATUS_FILTERS = [PayoutStatus.PAID, PayoutStatus.PENDING, PayoutStatus.PAYROLL, PayoutStatus.EXPIRED, PayoutStatus.CASH];

// The URL query params for one filter control's value; none for a cleared control.
const toFilterParams = (key: string, value: IPayPeriod | string[] | string | null): IApiQueryParam[] => {
  if (!value) return [];
  if (key === 'payment_date') return payPeriodDateFilters(value as IPayPeriod);
  if (key === 'status') {
    const statuses = value as string[];
    return [statuses?.length > 0 ? { key, operator: ApiQueryOperator.in, value: statuses } : { key, operator: ApiQueryOperator.nin, value: Object.values(PayoutStatus) }];
  }
  return (value as string).trim() ? [{ key, operator: ApiQueryOperator.like, value: value as string }] : [];
};

const ReportsPayrollTips = () => {
  const dispatch = useDispatch();
  const currentSite = useSelector(selectSite());
  const { organizationId: currentOrganizationId, siteId: currentSiteId } = useSelector(selectCurrentEmployee()) || {};
  const tips = useSelector(selectEmployeePayrollTips());
  const payoutStats = useSelector(selectPayoutStats());
  const isLoadingPayoutTips = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, EmployeePayrollTips.GET_EMPLOYEE_PAYROLL_TIPS_REQUEST));
  const isLoadingPayoutStats = useSelector((state: IStore) => selectIsLoadingAPIRequest(state, Stats.GET_SITE_PAYOUT_STATS_REQUEST));
  const canClosePayPeriod = useSelector(selectIsPermitted([EmployeeRole.ADMIN, EmployeeRole.SITE_ADMIN]));
  const canReopenPayPeriod = useSelector(selectIsPermitted([EmployeeRole.ADMIN]));

  // The URL holds the report's query: period bounds, status pills, name and employee ID filters, sort and page.
  const [urlQuery, setUrlQuery] = useQueryString('reportsPayrollTips', window.location, {
    limit: DEFAULT_PAGE_SIZE,
    offset: DEFAULT_OFFSET,
    sort: DEFAULT_SORT,
    q: [{ key: 'status', operator: ApiQueryOperator.in, value: DEFAULT_STATUS_FILTERS }],
  });

  // Replace one filter in the URL and return to the first page.
  const onFilterChange = (key: string, value: IPayPeriod | string[] | string | null) => setUrlQuery({ q: [...(urlQuery.q || []).filter((query: IApiQueryParam) => query.key !== key), ...toFilterParams(key, value)], offset: 0 });
  const debounceOnNameFilterChange = useDebounce((newValue: string) => onFilterChange('employee_name', newValue), 500);
  const debounceOnEmployeeIdFilterChange = useDebounce((newValue: string) => onFilterChange('employee.sites.employee_id', newValue), 500);

  // The URL names the selected period only when it is not the newest, so a plain URL opens on the current period.
  const urlPayPeriodStart = getQueryStringValue(urlQuery.q, 'payment_date', ApiQueryOperator.gte) as string;
  const { payPeriods: paymentPeriods, isLoadingFirstPage: isLoadingFirstPayPeriodsPage, isLoadingMore: isLoadingMorePayPeriods, loadMore: loadMorePayPeriods } = usePayPeriods(currentOrganizationId, currentSiteId, urlPayPeriodStart);
  const newestPayPeriod = paymentPeriods[0];
  const selectedPayPeriodStart = urlPayPeriodStart || newestPayPeriod?.start;
  const selectedPayPeriodEnd = (getQueryStringValue(urlQuery.q, 'payment_date', ApiQueryOperator.lte) as string) || newestPayPeriod?.end;
  const selectedPayPeriod = paymentPeriods.find((period) => period.start === selectedPayPeriodStart && period.end === selectedPayPeriodEnd) || null;

  // Loads the selected period's payees and stats. The period bounds go in the path, so they are dropped from the query.
  const fetchReport = useCallback(() => {
    const query = { ...urlQuery, q: urlQuery.q?.filter((q: IApiQueryParam) => q.key !== 'payment_date') };
    dispatch(getEmployeePayrollTipsRequest(currentSiteId, selectedPayPeriodStart, selectedPayPeriodEnd, query));
    dispatch(getSitePayoutStatsRequest(currentSiteId, selectedPayPeriodStart, selectedPayPeriodEnd, query));
  }, [dispatch, currentSiteId, urlQuery, selectedPayPeriodStart, selectedPayPeriodEnd]);

  // Fetch on every query change. A period the site does not have, left by another site, is dropped from the URL.
  const isSelectedPayPeriodLoaded = !!selectedPayPeriod;
  useEffect(() => {
    if (!newestPayPeriod) return;
    if (isSelectedPayPeriodLoaded) fetchReport();
    else onFilterChange('payment_date', null);
  }, [newestPayPeriod?.start, isSelectedPayPeriodLoaded, fetchReport]);
  const onPayPeriodChange = (period: IPayPeriod) => onFilterChange('payment_date', period.start === newestPayPeriod.start ? null : period);

  const today = getTodayWithSiteTimezone(currentSite?.settings?.timezone).format('YYYY-MM-DD');
  const [showCloseModal, hideCloseModal] = useModal(
    () =>
      selectedPayPeriod &&
      currentSite && (
        <ClosePayPeriodModal
          site={currentSite}
          orgId={currentOrganizationId}
          payPeriod={selectedPayPeriod}
          today={today}
          onClose={(closure) => {
            hideCloseModal();
            if (hasCloseChangedStats(closure)) fetchReport();
          }}
        />
      ),
    [currentSite, currentOrganizationId, selectedPayPeriod, today, fetchReport]
  );

  const [showReopenModal, hideReopenModal] = useModal(
    () => selectedPayPeriod && <ReopenPayPeriodModal siteId={currentSiteId} orgId={currentOrganizationId} payPeriod={selectedPayPeriod} onClose={hideReopenModal} />,
    [currentSiteId, currentOrganizationId, selectedPayPeriod]
  );

  const [filterActive, setFilterActive] = useState<boolean>(false);
  const isLoading = isLoadingPayoutTips || isLoadingPayoutStats || isLoadingFirstPayPeriodsPage;
  const payeesTotal = tips?.total || 0;
  const statuses: string[] = (urlQuery.q || []).find((q: IApiQueryParam) => q.key === 'status' && q.operator === ApiQueryOperator.in)?.value || [];
  const appliedFilterCount = (urlQuery.q || []).filter((item: IApiQueryParam) => !['payment_date', 'status'].includes(item.key)).length;

  if (!currentSiteId) return null;

  return (
    <Section data-cy="table">
      {selectedPayPeriod && <PayPeriodBanner payPeriod={selectedPayPeriod} today={today} onClosePeriod={showCloseModal} onReopenPeriod={showReopenModal} canClose={canClosePayPeriod} canReopen={canReopenPayPeriod} />}

      <StyledSectionHeader isLoading={isLoading} title="Payees" value={`(${payeesTotal})`} zIndex={250}>
        <PayrollPeriodSection>
          <PayrollPaymentLabel>Payroll Period</PayrollPaymentLabel>
          <PayrollPeriodWrapper>
            <PayPeriodSelector payPeriods={paymentPeriods} selected={selectedPayPeriod} isLoading={isLoadingFirstPayPeriodsPage} isLoadingMore={isLoadingMorePayPeriods} onLoadMore={loadMorePayPeriods} onChange={onPayPeriodChange} />
          </PayrollPeriodWrapper>
          <HeaderActions>
            <FilterButton filterActive={filterActive} appliedFilters={appliedFilterCount} onClick={() => setFilterActive(!filterActive)} />
            <ButtonExportPayrollTipsCSVAsync disabled={!tips || tips.total === 0 || isLoading} startDate={selectedPayPeriodStart} endDate={selectedPayPeriodEnd} query={urlQuery} site={currentSite} payPeriod={selectedPayPeriod} />
          </HeaderActions>
        </PayrollPeriodSection>
      </StyledSectionHeader>

      <Filter isVisible={filterActive}>
        <FieldWrapper label="Name" data-cy="filter-name">
          <Input placeholder="Name" onChange={(e: React.FormEvent<HTMLInputElement>) => debounceOnNameFilterChange(e.currentTarget.value)} defaultValue={getQueryStringValue(urlQuery.q, 'employee_name', ApiQueryOperator.like)} />
        </FieldWrapper>

        <FieldWrapper label="Employee ID" data-cy="filter-employee">
          <Input
            placeholder="Employee ID"
            onChange={(e: React.FormEvent<HTMLInputElement>) => debounceOnEmployeeIdFilterChange(e.currentTarget.value)}
            defaultValue={getQueryStringValue(urlQuery.q, 'employee.sites.employee_id', ApiQueryOperator.like)}
          />
        </FieldWrapper>
      </Filter>

      <PaymentStatusPills isLoading={isLoading} payoutStats={payoutStats} activeStatuses={statuses} onStatusChange={(s: string[]) => onFilterChange('status', s)} />
      <PaymentOverviewStats isLoading={isLoading} payoutStats={payoutStats} />

      <PaginatedList total={payeesTotal} limit={urlQuery.limit} offset={urlQuery.offset} onPageChange={(options: IApiQueryOptions) => setUrlQuery(options)} isLoading={isLoading} standalone={false}>
        <PayrollTipsTable
          tips={tips?.data || []}
          currentSiteId={currentSiteId}
          selectedPayPeriod={selectedPayPeriod}
          isLoading={isLoading}
          currentSort={urlQuery.sort || ''}
          onSortChange={(newSort: string) => setUrlQuery({ sort: urlQuery.sort === newSort ? `-${urlQuery.sort}` : newSort, offset: 0 })}
        />
      </PaginatedList>
    </Section>
  );
};

export default ReportsPayrollTips;
